<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "CPTemp14",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    computed: {
        projectPages() {
            const list = this.projects.projects || [];
            const pages = [];
            for (let i = 0; i < list.length; i += 4) {
                pages.push(list.slice(i, i + 4));
            }
            return pages;
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': projects.style.colorScheme.header,
        '--body-color': projects.style.colorScheme.body,
        '--footer-color': projects.style.colorScheme.footer,
        '--heading-color': projects.style.heading.color,
        '--heading-size': projects.style.heading.size + 'px',
        '--heading-font': projects.style.heading.font,
        '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': projects.style.heading.underline ? 'underline' : 'none',
        '--label-color': projects.style.label.color,
        '--label-size': projects.style.label.size + 'px',
        '--label-font': projects.style.label.font,
        '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
        '--label-style': projects.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': projects.style.label.underline ? 'underline' : 'none',
        '--text-color': projects.style.text.color,
        '--text-size': projects.style.text.size + 'px',
        '--text-font': projects.style.text.font,
        '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
        '--text-style': projects.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
    }">
        <div v-for="(page, pageIdx) in projectPages" :key="pageIdx" class="sub-container page-unit">
            <!-- PAGE CONTENT -->
            <div class="content-wrapper">
                <div class="title-wrapper">
                    <h1 class="title">
                        <SpellCheckedText :value="projects.title || ''" @input="projects.title = $event" />
                    </h1>
                </div>

                <div class="project-rows">
                    <div class="content-row row-1">
                        <div class="project-card card-large">
                            <div v-if="page[0]" class="image-container">
                                <PhotoBox class="photobox" :project="page[0]"
                                    :bgColor="projects.style.colorScheme.body" />
                                <div class="title-overlay">
                                    <SpellCheckedText :value="page[0].projectName || ''"
                                        @input="page[0].projectName = $event" />
                                </div>
                                <div class="description-box">
                                    <SpellCheckedText :value="page[0].projectDetails || 'Project description...'"
                                        @input="page[0].projectDetails = $event" />
                                </div>
                            </div>
                        </div>

                        <div class="project-card card-small">
                            <div v-if="page[1]" class="image-container">
                                <PhotoBox class="photobox" :project="page[1]"
                                    :bgColor="projects.style.colorScheme.body" />
                                <div class="title-overlay">
                                    <SpellCheckedText :value="page[1].projectName || ''"
                                        @input="page[1].projectName = $event" />
                                </div>
                                <div class="description-box">
                                    <SpellCheckedText :value="page[1].projectDetails || 'Project description...'"
                                        @input="page[1].projectDetails = $event" />
                                </div>
                            </div>
                            <div v-else class="empty-card" />
                        </div>
                    </div>

                    <div class="content-row row-2">
                        <div class="project-card card-medium">
                            <div v-if="page[2]" class="image-container">
                                <PhotoBox class="photobox" :project="page[2]"
                                    :bgColor="projects.style.colorScheme.body" />
                                <div class="title-overlay">
                                    <SpellCheckedText :value="page[2].projectName || ''"
                                        @input="page[2].projectName = $event" />
                                </div>
                                <div class="description-box">
                                    <SpellCheckedText :value="page[2].projectDetails || ''"
                                        @input="page[2].projectDetails = $event" />
                                </div>
                            </div>
                            <div v-else class="empty-card" />
                        </div>

                        <div class="project-card card-large">
                            <div v-if="page[3]" class="image-container">
                                <PhotoBox class="photobox" :project="page[3]"
                                    :bgColor="projects.style.colorScheme.body" />
                                <div class="title-overlay">
                                    <SpellCheckedText :value="page[3].projectName || ''"
                                        @input="page[3].projectName = $event" />
                                </div>
                                <div class="description-box">
                                    <SpellCheckedText :value="page[3].projectDetails || ''"
                                        @input="page[3].projectDetails = $event" />
                                </div>
                            </div>
                            <div v-else class="empty-card" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="page-footer">
                <div class="footer-left">
                    <SpellCheckedText :value="projects.opprName || ''" @input="projects.opprName = $event" />
                </div>
                <div class="footer-right">
                    <span class="company-name">
                        <SpellCheckedText :value="projects.submittedBy || ''" @input="projects.submittedBy = $event" />
                    </span>
                    <span class="divider">|</span>
                    <span class="page-num">{{ startPage + pageIdx }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 297mm;
    min-height: 210mm;
    box-sizing: border-box;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: column;
        background: white;
        width: 297mm;
        height: 210mm;
        padding-top: 30px;
        position: relative;
        overflow: hidden;
        box-sizing: border-box;
    }

    .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .title-wrapper {
        position: absolute;
        top: 25px;
        left: 40px;

        .title {
            font-size: var(--heading-size);
            font-weight: var(--heading-weight);
            color: var(--heading-color);
            font-family: var(--heading-font);
            font-style: var(--heading-style);
            text-decoration: var(--heading-decoration);
        }
    }

    .project-rows {
        display: flex;
        flex-direction: column;
        gap: 30px;
        padding: 0 30px;
        flex: 1;
    }

    .content-row {
        display: flex;
        gap: 30px;
        width: 100%;

        &.row-1 {
            height: 320px;

            .card-large {
                flex: 1.2;
                height: 250px;
                margin-top: auto;
                margin-left: 150px;
            }

            .card-small {
                flex: 1;
            }
        }

        &.row-2 {
            height: 350px;

            .card-medium {
                height: 100%;
                flex: 1;
            }

            .card-large {
                flex: 1;
            }
        }
    }

    .project-card {
        position: relative;
        overflow: hidden;
        background: var(--body-color);

        .image-container {
            width: 100%;
            height: 100%;
            position: relative;
        }

        .title-overlay {
            position: absolute;
            top: 15px;
            right: 15px;
            text-align: right;
            z-index: 10;
            color: var(--label-color);
            font-size: var(--label-size);
            font-weight: var(--label-weight);
            font-style: var(--label-style);
            text-decoration: var(--label-decoration);
            font-family: var(--label-font);
        }

        .description-box {
            position: absolute;
            bottom: 0;
            left: 0;
            z-index: 10;
            width: 100%;
            height: 50px;
            overflow: hidden;
            background: var(--header-color);
            padding: 5px 10px;
            color: var(--text-color);
            font-size: var(--text-size);
            font-weight: var(--text-weight);
            font-style: var(--text-style);
            text-decoration: var(--text-decoration);
            font-family: var(--text-font);
            white-space: pre-wrap;
        }
    }

    .empty-card {
        width: 100%;
        height: 100%;
        background: var(--body-color);
    }

    .page-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 15px;
        padding: 20px 50px;
        font-family: Helvetica;
        font-size: 12px;
        color: var(--footer-color);
        font-weight: bold;

        .footer-right {
            display: flex;
            align-items: center;

            .divider {
                margin: 0;
                padding: 0px 10px;
            }

            .page-num {
                font-size: 16px;
                color: var(--heading-color);
            }
        }
    }
}
</style>
